/**
 * @file   src\components\AgentWantAd\WantAdType.js
 * @brief  Two-level RESO property category + type selection for Buy Want-Listings.
 *         Category (radio, single-select) then Property Type (checkbox, multi-select).
 * @date   February, 2026
 */
import React, { useState, useEffect } from "react";
import _ from "lodash";
import { getPropertyCategoriesWithTypes } from "../../services/commonServices";
import {
  WANT_AD_BUY_PROPERTY_ID,
  PROPERTY_TYPE_TO_CATEGORY,
} from "../../constants/common";
import { convertToNumber } from "../../helpers/validations";

const WantAdType = (props) => {
  const {
    handleFormValues,
    propertyCategory,
    clearPreviousStates,
    propertyTypeError,
    prptySubTypes,
  } = props;

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    getPropertyCategoriesWithTypes("buy")
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const cats = _.get(response, "result", null);
          setCategories(cats);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }, []);

  // Infer category from existing prptySubTypes on edit (backwards compat)
  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      if (propertyCategory) {
        setSelectedCategory(propertyCategory);
      } else if (prptySubTypes && prptySubTypes.length > 0) {
        const inferredCategory = PROPERTY_TYPE_TO_CATEGORY[prptySubTypes[0]];
        if (inferredCategory) {
          setSelectedCategory(inferredCategory);
        }
      }
    }
  }, [categories, propertyCategory, prptySubTypes]);

  /**
   * Handle category radio selection
   */
  const handleCategorySelect = (categoryId, categoryText) => {
    if (categoryId === selectedCategory) return;
    setSelectedCategory(categoryId);
    clearPreviousStates({
      type: "clearErrorStateOnPropertySelection",
      keepRequirements: !selectedCategory,
    });
    handleFormValues({
      propertyCategory: categoryId,
      propertyCategoryTxt: categoryText,
      prptySubTypes: [],
      prprtyType: WANT_AD_BUY_PROPERTY_ID,
      prptyTypeTxt: "BUY",
      focusOut: false,
    });
  };

  /**
   * Handle property type checkbox toggle within selected category
   */
  const handleTypeToggle = (typeId) => {
    let updatedSubTypes;
    if (_.includes(prptySubTypes, typeId)) {
      updatedSubTypes = prptySubTypes.filter((id) => id !== typeId);
    } else {
      updatedSubTypes = [...(prptySubTypes || []), typeId];
    }
    handleFormValues({
      prptySubTypes: updatedSubTypes,
      prprtyType: WANT_AD_BUY_PROPERTY_ID,
      prptyTypeTxt: "BUY",
      focusOut: true,
    });
  };

  // Get the types for the currently selected category
  const selectedCategoryData = categories.find(
    (c) => c.CategoryId === selectedCategory
  );

  return (
    <>
      {/* Property Category - radio selection */}
      <h6 className={propertyTypeError && !selectedCategory ? "red-marked-heading" : ""}>
        Property Category <span>*</span>
      </h6>
      <div className="wantad-type-wrap mb-3">
        {categories.map((cat) => (
          <label className="radio-wrap mb-3" key={cat.CategoryId}>
            <input
              type="radio"
              name="property_category_radio"
              value={cat.CategoryId}
              checked={selectedCategory === cat.CategoryId}
              onChange={() =>
                handleCategorySelect(cat.CategoryId, cat.CategoryText)
              }
            />
            <span className={propertyTypeError && !selectedCategory ? "red-marked-span" : ""}>
              <div className="wantad-type-info">
                <h2
                  className={
                    propertyTypeError && !selectedCategory
                      ? "pb-0 red-marked-subheading"
                      : "pb-0 wanted-info-list"
                  }
                >
                  {cat.CategoryText}
                </h2>
              </div>
            </span>
          </label>
        ))}
      </div>

      {/* Property Type - checkbox multi-select (shown after category selected) */}
      {selectedCategoryData && (
        <>
          <h6
            className={
              propertyTypeError && selectedCategory && (!prptySubTypes || prptySubTypes.length === 0)
                ? "red-marked-heading"
                : ""
            }
          >
            Property Type <span>*</span>
          </h6>
          <div className="wantad-type-wrap">
            {selectedCategoryData.types.map((pt) => {
              const typeId = pt.PropertyTypeId;
              const isChecked =
                prptySubTypes && _.includes(prptySubTypes, typeId);
              return (
                <label className="check-wrap mb-3" key={typeId}>
                  <input
                    type="checkbox"
                    name="property_type_checkbox"
                    id={typeId}
                    value={typeId}
                    checked={isChecked}
                    onChange={() => handleTypeToggle(typeId)}
                  />
                  <span
                    className={
                      propertyTypeError &&
                      selectedCategory &&
                      (!prptySubTypes || prptySubTypes.length === 0)
                        ? "red-marked-span"
                        : ""
                    }
                  >
                    <div className="wantad-type-info">
                      <h2
                        className={
                          propertyTypeError &&
                          selectedCategory &&
                          (!prptySubTypes || prptySubTypes.length === 0)
                            ? "pb-0 red-marked-subheading"
                            : "pb-0 wanted-info-list"
                        }
                      >
                        {pt.PropertyTypeText}
                      </h2>
                    </div>
                  </span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </>
  );
};
export default WantAdType;
